// no itx for this file
import { getTreeviewHtmlElement, revealNavigatorObject } from "../components/NavigationTree2";
import { navigatorDebugObject } from "../components/NavigationTree2/navigatorDebug";
import TreeMenuIcon from "../components/NavigationTree2/TreeMenuIcon";
import BaseModel from "./BaseModel";
import BaseModelReference from "./BaseModelReference";
import TreeNodeInfo from "./TreeNodeInfo";
import Logger from "../Logger";
import { showErrToast, showToast } from "../components/Toasts";
import FlyoutControl from "../components/FlyoutControl";
import BaseModelMap from "./BaseModelMap";
import BaseObject, { enumCompareStatus } from "./BaseObject";
import { sortArrayByProperty } from "./utils";
import { applyChanges } from "../CompareProjects";
import { WorkingPane } from "./ActivePanes";
import Room from "./Room";
import { itx } from "../translationItx/itx";

const logger = new Logger();

export enum DragMode {
  copy = "copy",
  move = "copyMove",
  link = "link",
}

export class DropResult {
  constructor(success: boolean, errorMessage?: string) {
    this.success = success;
    this.errorMessage = errorMessage;
  }
  success: boolean = false;
  errorMessage: string | undefined;
}

export default class TreeViewModel {
  public get className(): string {
    return this.constructor.name;
  }

  obj: BaseModel;
  buttons: JSX.Element[] = [];

  getButtons(): JSX.Element[] | undefined {
    return [];
  }

  getButtonsWithFlyout(node: TreeNodeInfo, includeProperties = true): JSX.Element[] | undefined {
    const b = this.getButtons() || [];
    // if (!node.hasCompareStatus(enumCompareStatus.equal)) {

    const contentOrStructureModified =
      node.obj.compareStatus &
      (enumCompareStatus.deletedInCurrentProject |
        enumCompareStatus.deletedInOtherProject |
        enumCompareStatus.modified |
        enumCompareStatus.newInCurrentProject |
        enumCompareStatus.newInOtherProject);
    if (contentOrStructureModified) {
      b.push(this.inlineButton_ApplyChange(node));
    }
    const fo = includeProperties ? this.inlineButton_Flyout() : undefined;
    if (fo) {
      b.push(fo);
    }
    return b;
  }

  get isFocusedInNavigator(): boolean {
    return state.selectionManager.focusedObjectInNavigator === this.obj;
  }

  get maxChildColumnSizes(): Array<number> {
    return [];
  }

  get isSelectable(): boolean {
    return true;
  }

  constructor(obj: BaseModel) {
    this.obj = obj;
  }

  get isFolder(): boolean {
    return false;
  }

  getHasChildren(parentNodeInfo: TreeNodeInfo): boolean {
    const ccx = this.getChildCollectionEx(new TreeNodeInfo(parentNodeInfo, this.obj));
    if (ccx) {
      if (ccx.length > 0) {
        return true;
      }
    }

    if (state.showModifiedStatus && this.deletedChildCollection.length > 0) {
      return true;
    }

    if (
      globalThis.compareState &&
      (this.deletedChildCollection.length > 0 || this.newInOtherChildCollection.length > 0)
    ) {
      return true;
    }

    return false;
  }

  get hasChildren(): boolean {
    const ccx = this.getChildCollectionEx(new TreeNodeInfo(undefined, this.obj));
    if (ccx) {
      if (ccx.length > 0) {
        return true;
      }
    }

    if (state.showModifiedStatus && this.deletedChildCollection.length > 0) {
      return true;
    }

    return false;
  }

  getWaitingForChildren(): boolean {
    return false;
  }

  getFullTitle(isExpanded = false): string | undefined | JSX.Element {
    return undefined;
  }

  getTitlePostfix(): string | JSX.Element {
    return "";
  }

  getTitlePrefix(): string | JSX.Element {
    return "";
  }

  getNameNeeded(): boolean {
    return true;
  }

  getTagNeeded(): boolean {
    return true;
  }

  getTag(): string {
    return this.obj.tag;
  }

  getDisableEdit(): boolean {
    return false;
  }
  getDisableEditName(): boolean {
    return false;
  }
  getDisableEditTag(): boolean {
    return false;
  }

  getTagStyle(): any | undefined {
    return undefined;
  }

  get childCollection(): Array<BaseModel> | undefined {
    return undefined;
  }

  getChildCollectionEx(parentTreeNodeInfo: TreeNodeInfo): Array<TreeNodeInfo> | undefined {
    const children = this.childCollection;
    if (children) {
      const childrenEx = new Array<TreeNodeInfo>();
      children.forEach((child) => {
        if (parentTreeNodeInfo.obj == child || parentTreeNodeInfo.isChildOf(child)) {
          return;
        }

        const newChild = new TreeNodeInfo(parentTreeNodeInfo, child);
        newChild.parentTreeNodeInfo = parentTreeNodeInfo;
        childrenEx.push(newChild);
      });

      return childrenEx;
    }

    return undefined;
  }

  get deletedChildCollection(): Array<BaseModel> {
    let res = new Array<BaseModel>();

    if (this.obj.className == "BaseModelMap") {
      const collection = this.obj as any as BaseModelMap<BaseModel>;
      const deletedItems = collection?.deletedItems;
      if (deletedItems) {
        const arrDeleted: Array<BaseModel> = Array.from(deletedItems.values());
        res = res.concat(sortArrayByProperty<BaseModel>(arrDeleted, "address"));
      }
    }

    const props = this.obj.getPersistentProperties();
    for (const prop of props) {
      const collection = (this.obj as any)[prop] as BaseModelMap<BaseModel>;
      if (collection?.className == "BaseModelMap") {
        const deletedItems = collection?.deletedItems;
        if (deletedItems) {
          const arrDeleted: Array<BaseModel> = Array.from(deletedItems.values());
          res = res.concat(sortArrayByProperty<BaseModel>(arrDeleted, "address"));
        }
      }
    }
    return res;
  }

  get newInOtherChildCollection(): Array<BaseModel> {
    let res = new Array<BaseModel>();

    const collection = this.obj as any as BaseModelMap<BaseModel>;
    const newInOtherProjectItems = collection?.newInOtherProjectItems;
    if (newInOtherProjectItems) {
      const arrNew: Array<BaseModel> = Array.from(newInOtherProjectItems.values());
      res = res.concat(sortArrayByProperty<BaseModel>(arrNew, "address"));
    }

    const props = this.obj.getPersistentProperties();
    for (const prop of props) {
      const collection = (this.obj as any)[prop] as BaseModelMap<BaseModel>;
      const newInOtherProjectItems = collection?.newInOtherProjectItems;
      if (newInOtherProjectItems) {
        const arrNew: Array<BaseModel> = Array.from(newInOtherProjectItems.values());
        res = res.concat(sortArrayByProperty<BaseModel>(arrNew, "address"));
      }
    }
    return res;
  }

  getIconUrl(): string | undefined {
    return this.obj.getIconUrl();
  }

  get iconUrl(): string | undefined {
    return this.getIconUrl();
  }

  getIcon(context?: any): any | undefined {
    let res: any = undefined;

    const url = this.iconUrl;
    if (url?.substring(0, 2) == "la") {
      res = (
        <div className="icon" draggable={false}>
          <i className={url} draggable={false}></i>
        </div>
      );
    }

    if (url && url?.substring(0, 2) != "la") {
      res = <img className={`icon navIcon`} src={url} draggable={false} />;
    }

    return res;
  }

  get icon(): any | undefined {
    return this.getIcon();
  }

  getFlyoutControl(): any {
    return this.getPropertyControl((pinned: boolean) => {});
  }

  getPropertyControl(setPinned: (pinned: boolean) => void): any {
    return null;
  }

  getPropertyHeader(): any {
    return null;
  }

  unselect() {
    state.selectionManager.selectedObject = undefined;

    const elm: any = document.activeElement;

    if (elm && elm.blur) {
      elm.blur();
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////

  //
  // returns true if this item support double click.
  // if not, doubleclick is handled as "expand click"
  //

  handleDoubleClick(): boolean {
    if (this.hasOpenButton) {
      return this.handleOpen();
    }

    return false;
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////
  //
  // Context Menu Section
  //
  //////////////////////////////////////////////////////////////////////////////////////////////////

  get contextMenuItems(): any | undefined {
    return undefined;
  }

  async getDisabledContextMenuItems(): Promise<Array<string>> {
    return new Array<string>();
  }

  /**
   * Override to add extra context menu items in addition to the auto-derived button items.
   * Items have the shape { id, iconName, label, onClick, ... } (same as baseContextMenuItems_*).
   */
  getExtraContextMenuItems(): any[] {
    return [];
  }

  /**
   * Returns the context menu representation of the buttons from getButtons().
   * Default: auto-converts all TreeMenuIcon buttons via buttonsAsContextMenuItems().
   *
   * Override in subclasses when a button opens a sub-menu via show1() — return that button
   * with a `menu: { items: [...] }` property so it renders as a native <Submenu> instead of
   * triggering a replacement context menu.
   */
  getButtonsAsContextMenuItems(): any[] {
    return this.buttonsAsContextMenuItems();
  }

  /**
   * Converts the JSX elements returned by getButtons() into context menu item objects.
   * Only TreeMenuIcon elements (those with an iconName prop) are converted.
   *
   * react-contexify calls Item onClick with { event, props, data, triggerEvent }.
   * We unwrap the real DOM MouseEvent so that handlers which call show1() receive
   * the correct event for positioning the follow-up menu.
   */
  buttonsAsContextMenuItems(): any[] {
    const buttons = this.getButtons() || [];
    const result: any[] = [];
    for (const btn of buttons) {
      if (!btn || !btn.props || !btn.props.iconName) continue;
      const props = btn.props as {
        iconName: string;
        onClick: any;
        tooltipText?: string;
        tooltipKey?: string;
      };
      let label: string;
      if (props.tooltipKey) {
        label = state.translate(props.tooltipKey, props.tooltipKey);
      } else if (props.tooltipText) {
        label = props.tooltipText;
      } else {
        label = itx("contextMenu." + props.iconName, props.iconName);
      }
      const rawOnClick = props.onClick;
      result.push({
        id: "btn_" + props.iconName,
        iconName: props.iconName,
        label: label,
        // react-contexify passes { event: MouseEvent, props, data, triggerEvent } to Item onClicks.
        // Unwrap to hand the real DOM event to handlers that use it for positioning (e.g. show1()).
        onClick: (contexifyArgs: any) => {
          const realEvent =
            contexifyArgs && typeof contexifyArgs === "object" && "event" in contexifyArgs
              ? contexifyArgs.event
              : contexifyArgs;
          rawOnClick(realEvent);
        },
      });
    }
    return result;
  }

  async getClipboradData(): Promise<any | undefined> {
    try {
      let text = await navigator.clipboard.readText();
      const json = JSON.parse(text);
      logger.log("getClipboradData", json);

      if (json.operation === undefined) {
        return undefined;
      } else {
        state.contextMenuManager.clipboardData = json;
        return json;
      }
    } catch (err) {}

    return undefined;
  }

  canBePastedInteractive(copiedObjClassName: string): boolean {
    return false;
  }

  //////////////////////////////////////////////////////////////////////
  // handle for buttons and context items
  //////////////////////////////////////////////////////////////////////

  async handleCopy() {
    state.menubarActions.onCopy(this.obj);
  }

  async handlePaste() {
    state.menubarActions.onPaste(this.obj);
  }

  handleDelete() {
    state.menubarActions.onDelete(this.obj);
  }

  handleDeconnect() {
    logger.error(`handleAdd not implemented for ${this.className}`);
  }

  handleCut() {
    state.menubarActions.onCopy(this.obj);
    state.menubarActions.onDelete(this.obj);
    this.obj.delete();
  }

  handleProperties() {
    logger.error(`handleProperties not implemented for ${this.className}`);
  }

  handleClose(onlyIfTransitOpen: boolean = false): boolean {
    if (this.hasOpenButton) {
      let id = this.obj.id;

      if (this.obj.className == "Room") {
        id = (this.obj as Room).page?.id || "";
      }

      if (state.activePanes.isOpen(id)) {
        const pane = state.activePanes.getPane(id);
        if (pane) {
          if (onlyIfTransitOpen && !pane.isTransitOpen) {
            return false;
          }
        }

        state.activePanes.closePane(id);
        return true;
      }
    }
    return false;
  }

  handleOpen(transitOpen: boolean = false): boolean {
    const id = this.obj.id;
    return this.handleOpenId(id, transitOpen);
  }

  handleOpenId(id: string, transitOpen: boolean = false): boolean {
    if (this.hasOpenButton) {
      return this.openDevice(id, transitOpen);
    } else {
      return false;
    }
  }

  openDevice(id: string, transitOpen: boolean = false): boolean {
    let pane: WorkingPane | undefined;

    if (state.activePanes.isOpen(id)) {
      pane = state.activePanes.activatePane(id);
    } else {
      const objFromId = state.objectRegistry.getObject(id);
      if (objFromId && objFromId instanceof BaseModel) {
        pane = state.activePanes.openPane(objFromId);
      } else {
        pane = state.activePanes.openPane(this.obj);
      }
    }

    if (pane) {
      if (!pane.isTransitOpen) {
        //
        // if we open a second time,
        // iE click 2x Enter in the tree its premanent open
        //
        pane.isTransitOpen = transitOpen;
      } else {
        pane.isTransitOpen = false;
      }
      pane.tabItemData?.raiseOnChange("isTransitOpen");
    }
    return true;
  }

  handleAdd() {
    logger.error(`handleAdd not implemented for ${this.className}`);
  }

  /**
   * Called when the Insert key is pressed on the focused tree node.
   * Defaults to handleAdd(). Override in subclasses where the + button opens a
   * positioned dropdown (show1) — pass the event so the menu can be positioned.
   */
  handleAddWithEvent(event: any) {
    this.handleAdd();
  }

  informationText(): string {
    return `handleInformation not implemented for ${this.className}`;
  }

  //////////////////////////////////////////////////////////////////////
  // baseContextMenuItems
  //////////////////////////////////////////////////////////////////////

  baseContextMenuItems_Add(label: string, onClick?: () => void) {
    return {
      id: "add",
      iconName: "plus-circle",
      label: label,
      onClick: () => {
        if (onClick) {
          onClick();
        } else {
          this.handleAdd();
        }
      },
    };
  }

  baseContextMenuItems_Copy() {
    return {
      id: "copy",
      iconName: "copy",
      label: "ContextMenuLables.copy",
      onClick: () => this.handleCopy(),
    };
  }

  baseContextMenuItems_Cut() {
    return {
      id: "cut",
      iconName: "cut",
      label: "ContextMenuLables.cut",
      onClick: () => {
        this.handlePaste();
        this.handleCut();
      },
    };
  }

  baseContextMenuItems_Paste(subLabelPaste: string | undefined) {
    return {
      id: "paste",
      iconName: "paste",
      label: "ContextMenuLables.paste",
      subLabel: subLabelPaste,
      onClick: () => {
        this.handlePaste();
      },
    };
  }

  baseContextMenuItems_Delete() {
    return {
      id: "delete",
      iconName: "trash",
      label: "ContextMenuLables.delete",
      onClick: () => {
        this.handleDelete();
      },
    };
  }

  baseContextMenuItems_Properties() {
    return {
      id: "settings",
      iconName: "gear",
      label: "ContextMenuLables.settings",

      onClick: () => {
        this.handleProperties();
      },
    };
  }

  baseContextMenuItems_Open() {
    return {
      id: "open",
      iconName: "edit",
      label: "ContextMenuLables.open",
      onClick: () => {
        this.handleOpen();
      },
    };
  }

  //////////////////////////////////////////////////////////////////////
  // inlineButton
  //////////////////////////////////////////////////////////////////////

  inlineButton_Add(tooltipKey: string) {
    return (
      <TreeMenuIcon
        tooltipKey={tooltipKey}
        onClick={() => {
          this.handleAdd();
        }}
        iconName="plus-circle"
        objName={this.obj.className + this.obj.fullTag}
        key={this.obj.className + this.obj.fullTag + "add"}
      />
    );
  }

  inlineButton_ApplyChange(node: TreeNodeInfo) {
    return (
      <TreeMenuIcon
        onClick={() => {
          applyChanges(node);
        }}
        iconName="exchange-alt"
        tooltipKey={"Änderungen Übernehmen"}
        objName={this.obj.className + this.obj.fullTag}
        key={this.obj.className + this.obj.fullTag + "deConnect"}
      />
    );
  }

  inlineButton_Properties() {
    return (
      <TreeMenuIcon
        onClick={() => {
          this.handleProperties();
        }}
        iconName="edit"
        objName={this.obj.className + this.obj.fullTag}
        key={this.obj.className + this.obj.fullTag + "properties"}
      />
    );
  }

  hasOpenButton = false;
  inlineButton_Open(tooltipKey: string, iconName?: string) {
    this.hasOpenButton = true;
    return (
      <TreeMenuIcon
        onClick={() => {
          this.handleOpen();
        }}
        iconName={iconName || "edit"} //  "file", // "edit",
        tooltipKey={tooltipKey}
        objName={this.obj.className + this.obj.fullTag}
        key={this.obj.className + this.obj.fullTag + "open"}
      />
    );
  }

  inlineButton_Delete(tooltipKey?: string) {
    return (
      <TreeMenuIcon
        onClick={() => {
          this.handleDelete();
        }}
        iconName="trash"
        tooltipKey={tooltipKey}
        objName={this.obj.className + this.obj.fullTag}
        key={this.obj.className + this.obj.fullTag + "delete"}
      />
    );
  }
  inlineButton_Deconnect(tooltipKey?: string) {
    return (
      <TreeMenuIcon
        onClick={() => {
          this.handleDeconnect();
        }}
        iconName="unlink"
        tooltipKey={tooltipKey}
        objName={this.obj.className + this.obj.fullTag}
        key={this.obj.className + this.obj.fullTag + "deConnect"}
      />
    );
  }

  inlineButton_Information() {
    return (
      <TreeMenuIcon
        onClick={() => {
          //  this.handleInformation();
        }}
        iconName="info-circle"
        tooltipText={this.informationText()}
        objName={this.obj.className + this.obj.fullTag}
        key={this.obj.className + this.obj.fullTag + "open"}
      />
    );
  }

  inlineButton_Reference(referencedObj?: BaseModel, tooltipText?: string) {
    return (
      <TreeMenuIcon
        onClick={() => this.navigateToReference(referencedObj)}
        iconName="external-link-alt" // "expeditedssl",
        objName={this.obj.className + this.obj.fullTag}
        tooltipText={tooltipText}
        key={this.obj.className + this.obj.fullTag + "_inlineButton_nav1"}
      />
    );
  }

  async navigateToReference(referencedObj?: BaseModel) {
    try {
      if (!referencedObj) {
        referencedObj = (this.obj as BaseModelReference).referencedObject as BaseModel;
        if (referencedObj?.className == "Page") {
          referencedObj = referencedObj.parent as BaseModel;
        }
      }

      console.log("[NavigatorDebug] navigateToReference: target", {
        reference: navigatorDebugObject(this.obj), target: navigatorDebugObject(referencedObj),
      });
      if (!referencedObj) return;
      state.selectionManager.selectedObject = referencedObj;
      await revealNavigatorObject(referencedObj.id);
      if (state.selectionManager.selectedObject !== referencedObj) {
        console.log("[NavigatorDebug] navigateToReference: selection changed", {
          target: navigatorDebugObject(referencedObj), selected: navigatorDebugObject(state.selectionManager.selectedObject),
        });
        return;
      }
      const elm = getTreeviewHtmlElement("navigator", referencedObj);
      console.log("[NavigatorDebug] navigateToReference: target row", { target: navigatorDebugObject(referencedObj), rowFound: !!elm });
      elm?.scrollIntoView({ block: "nearest", inline: "nearest" });
      elm?.focus({ preventScroll: true });
    } catch (ex) {
      logger.error(ex);
    }
  }

  inlineButton_Flyout() {
    const foc = this.getFlyoutControl();
    if (foc) {
      return <FlyoutControl obj={this.obj} foc={foc} key={this.obj.className + this.obj.fullTag + "flyout"} />;
    }

    return undefined;
  }

  //////////////////////////////////////////////////////////////////////
  // Drag
  //////////////////////////////////////////////////////////////////////
  allowDrag(): boolean {
    return false;
  }

  dragStart(e: any, effectAllowed: string = "copyMove") {
    const xobj =
      this.obj.className == "BaseModelReference" || this.obj.className == "FuseConsumerReference"
        ? (this.obj as BaseModelReference).referencedObject
        : this.obj;
    if (!xobj) {
      showErrToast("drag start failed, no object to drag");
      return;
    }

    const xdata = JSON.stringify({
      className: xobj.className,
      id: xobj.id,
    });

    e.dataTransfer.setData("text/json", xdata);
    e.dataTransfer.effectAllowed = effectAllowed;

    // set local dataTransferObject
    state.dragData = xdata;
    state.dragEffectAllowed = effectAllowed;

    // const img = document.createElement("img");
    // img.style.width = "32px";
    // img.style.height = "32px";
    // img.src = "images/buttons/touch-6.png";

    // const xOffset = 16;
    // const yOffset = 16;
    // e.dataTransfer.setDragImage(img, xOffset, yOffset);

    logger.log("dragStart done", xdata);
  }

  //////////////////////////////////////////////////////////////////////
  // Drop
  //////////////////////////////////////////////////////////////////////

  allowDrop(event: any): boolean {
    return true;
  }

  async handleDrop2(mode: DragMode, obj: BaseModel): Promise<DropResult> {
    try {
      if (mode == DragMode.copy) {
        // copy object
        await state.menubarActions.onCopy(obj);
        await state.menubarActions.onPaste(this.obj);
        return new DropResult(true);
      } else {
        // move obj
        const moved = obj.moveToNewParent(this.obj);
        if (moved) {
          // force reselction with exapnd and scroll
          state.selectionManager.selectedObject = undefined;
          //////////////////////////////////////////////////
          setTimeout(() => {
            state.selectionManager.selectedObject = obj;
          }, 100);
          return new DropResult(true);
        } else {
          return new DropResult(false);
        }
      }
    } catch (ex) {
      logger.error(ex);
    }
    return new DropResult(false);
  }

  async handleDrop(mode: DragMode, sData: string) {
    let txt = state.translate("treeViewModel.dragADropforbidden", `Drag&Drop ist nicht erlaubt`);
    try {
      const data = JSON.parse(sData);

      if (data.id) {
        const obj = state.objectRegistry.getObject(data.id) as BaseModel;
        if (obj) {
          const res = await this.handleDrop2(mode, obj);
          if (res.errorMessage) {
            txt += ":\n" + res.errorMessage;
          }
          if (res.success) {
            if (res.errorMessage) {
              showToast({
                isError: false,
                text: res.errorMessage,
                time: 5000,
              });
            }
            return;
          }
        }
      }
    } catch (ex) {
      logger.error(ex);
    }

    showToast({
      isError: true,
      text: txt,
    });
  }

  onDrop(e: any) {
    e.preventDefault();
    e.stopPropagation();

    try {
      var items = e.dataTransfer.items;
      const item = items[0];
      if (item.kind === "string") {
        const self = this;
        item.getAsString(function (sData: string) {
          const mode = e.dataTransfer.effectAllowed;
          self.handleDrop(mode, sData);
        });
      }
    } catch (ex) {
      logger.error(ex);
    }
  }
}
